import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Autocomplete,
  Grid,
  Paper,
  Container,
  Snackbar,
  Alert,
  Box,
  IconButton,
  Divider,
  List,
  Chip
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useOffice } from '../../store/officeContext';
import { supabase } from '../../supabaseClient';
import { shareCaseDriveFolder } from '../../constants/CommonFunctions';
import MemberCard from '../../components/Card/Member/MemberCard';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Delete } from '@mui/icons-material';

// Textos en diferentes idiomas
const texts = {
  es: {
    title: 'Crear Nuevo Expediente',
    caseNumber: 'Nº de expediente',
    practiceArea: 'Área Práctica',
    form: 'Forma',
    caseType: 'Tipo de expediente',
    status: 'Estatus',
    caseLanguage: 'Idioma del expediente',
    spanish: 'Español',
    english: 'Inglés',
    caseTitle: 'Título',
    description: 'Descripción',
    assignedCollaborator: 'Abogado asignado (opcional)',
    userIdentified: 'Usuario identificado',
    userNotIdentified: 'Usuario no identificado',
    selectedClient: 'Cliente Seleccionado',
    assignedPartner: 'Abogado Asignado',
    assignedCollaboratorTitle: 'Abogado Asignado',
    createCase: 'Crear expediente',
    requiredField: 'Campo requerido',
    numberRequired: 'Número requerido',
    practiceAreaRequired: 'Área práctica requerida',
    formRequired: 'Forma requerida',
    typeRequired: 'Tipo requerido',
    statusRequired: 'Estatus requerido',
    languageRequired: 'Idioma requerido',
    titleRequired: 'Título requerido',
    descriptionRequired: 'Descripción requerida',
    clientRequired: 'Cliente requerido',
    clientEmail: 'Correo o DNI del cliente',
    clientOrTextRequired: 'Debe seleccionar un cliente o ingresar un identificador',
    caseCreatedSuccess: 'Expediente creado exitosamente.',
    saveError: 'Ocurrió un error al guardar el expediente.',
    unexpectedError: 'Error inesperado',
    generateError: 'Error al generar número de expediente',
    selectPracticeAreaFirst: 'Seleccione primero un área práctica',
    selectFormFirst: 'Seleccione primero una forma',
    selectTypeFirst: 'Seleccione primero un tipo (si aplica)',
    noTypesAvailable: 'Esta forma no usa tipos',
    noStatusAvailable: 'No hay estatus disponibles',
    tasksWillBeCreated: 'tareas se crearán automáticamente',
    taskWillBeCreated: 'tarea se creará automáticamente',
    noTaskTemplate: 'Este estatus no tiene tareas plantilla configuradas',
    basicInfo: 'Información Básica',
    classification: 'Clasificación',
    dates: 'Fechas',
    parties: 'Partes Involucradas',
    startDate: 'Fecha de inicio',
    endDate: 'Fecha de finalización',
    optional: 'Opcional',
    addMember: 'Agregar',
    memberRole: 'Rol',
    searchMember: 'Buscar persona o ingresar nombre',
    caseMembers: 'Miembros del Expediente',
    noMembersYet: 'No se han agregado miembros',
    removeMember: 'Eliminar',
    clientRole: 'Cliente',
    partnerRole: 'Abogado',
    lawyerRole: 'Colaborador',
    memberAlreadyAdded: 'Esta persona ya fue agregada',
    onlyOneClient: 'Solo puede haber un cliente',
    onlyOnePartner: 'Solo puede haber un abogado',
    mustAddClient: 'Debe agregar al menos un cliente',
    systemUser: 'Usuario del sistema',
    textLabel: 'Etiqueta de texto',
    canRegisterLater: 'Este miembro se agregará como texto. Podrá registrarlo posteriormente y vincularlo al expediente.',
    mustEnterName: 'Debe ingresar un nombre o seleccionar una persona',
    systemUser: 'Usuario del sistema',
    textLabel: 'Etiqueta de texto',
    canRegisterLater: 'Este miembro se agregará como texto. Podrá registrarlo posteriormente y vincularlo al expediente.',
    canBeLinkedLater: 'Podrá registrarse y vincularse posteriormente',
  },
  en: {
    title: 'Create New Case',
    caseNumber: 'Case number',
    practiceArea: 'Practice Area',
    form: 'Form',
    caseType: 'Case type',
    status: 'Status',
    caseLanguage: 'Case language',
    spanish: 'Spanish',
    english: 'English',
    caseTitle: 'Title',
    description: 'Description',
    clientEmail: 'Client email or ID',
    assignedCollaborator: 'Assigned lawyer (optional)',
    userIdentified: 'User identified',
    userNotIdentified: 'User not identified',
    selectedClient: 'Selected Client',
    assignedPartner: 'Assigned Lawyer',
    assignedCollaboratorTitle: 'Assigned lawyer',
    createCase: 'Create case',
    requiredField: 'Required field',
    numberRequired: 'Number required',
    practiceAreaRequired: 'Practice area required',
    formRequired: 'Form required',
    typeRequired: 'Type required',
    statusRequired: 'Status required',
    languageRequired: 'Language required',
    titleRequired: 'Title required',
    descriptionRequired: 'Description required',
    clientRequired: 'Client required',
    clientOrTextRequired: 'You must select a client or enter an identifier',
    caseCreatedSuccess: 'Case created successfully.',
    saveError: 'An error occurred while saving the case.',
    unexpectedError: 'Unexpected error',
    generateError: 'Error generating case number',
    selectPracticeAreaFirst: 'Select a practice area first',
    selectFormFirst: 'Select a form first',
    selectTypeFirst: 'Select a type first (if applicable)',
    noTypesAvailable: 'This form does not use types',
    noStatusAvailable: 'No status available',
    tasksWillBeCreated: 'tasks will be created automatically',
    taskWillBeCreated: 'task will be created automatically',
    noTaskTemplate: 'This status has no task templates configured',
    classification: 'Classification',
    dates: 'Dates',
    parties: 'Parties',
    startDate: 'Start Date',
    endDate: 'End Date',
    optional: 'Optional',
    addMember: 'Add',
    memberRole: 'Role',
    searchMember: 'Search person or enter name',
    caseMembers: 'Case Members',
    noMembersYet: 'No members added yet',
    removeMember: 'Remove',
    clientRole: 'Client',
    partnerRole: 'Lawyer',
    lawyerRole: 'Partner',
    memberAlreadyAdded: 'This person has already been added',
    onlyOneClient: 'Only one client allowed',
    onlyOnePartner: 'Only one lawyer allowed',
    mustAddClient: 'Must add at least one client',
    systemUser: 'System user',
    textLabel: 'Text label',
    canRegisterLater: 'This member will be added as text. You can register them later and link them to the case.',
    mustEnterName: 'Must enter a name or select a person',
    systemUser: 'System user',
    textLabel: 'Text label',
    canRegisterLater: 'This member will be added as text. You can register them later and link them to the case.',
    canBeLinkedLater: 'Can be registered and linked later',
  }
};

export default function CaseRegisterView() {
  const { control, handleSubmit, setValue, watch, formState: { errors, isValid } } = useForm({
    mode: 'onChange',
    defaultValues: {
      idioma: 'spanish',
      numero: '',
      practiceArea: '',
      form: '',
      tipo: '',
      status: '',
      titulo: '',
      descripcion: '',
      start_date: '',
      end_date: '',
      client_str: '',
      descripcion: '',
      start_date: '',
      end_date: '',  
    }
  });

  const office = useOffice().office;
  const navigate = useNavigate();
  
  // Estados para la jerarquía: práctica → form → tipo → estatus
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [officeForms, setOfficeForms] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [officeStatuses, setOfficeStatuses] = useState([]);
  
  const [loadingPracticeAreas, setLoadingPracticeAreas] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingCaseTypes, setLoadingCaseTypes] = useState(false);
  const [loadingStatuses, setLoadingStatuses] = useState(false);

  // Estado para el idioma actual
  const [currentLanguage, setCurrentLanguage] = useState('es');
  const watchedLanguage = watch('idioma');
  const watchedPracticeArea = watch('practiceArea');
  const watchedForm = watch('form');
  const watchedType = watch('tipo');

  const [caseMembers, setCaseMembers] = useState([]); // Array de { person_id, name_str, role, full_name, email }
  const [memberOptions, setMemberOptions] = useState([]); // Opciones de búsqueda
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [currentMemberInput, setCurrentMemberInput] = useState(null); // Persona o string actual
  const [currentMemberRole, setCurrentMemberRole] = useState('client'); // Rol seleccionado

  const [statusTaskCount, setStatusTaskCount] = useState(0);

  // Estados para Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Función para mostrar snackbar
  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Función para cerrar snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Efecto para cambiar el idioma de la interfaz
  useEffect(() => {
    setCurrentLanguage(watchedLanguage === 'spanish' ? 'es' : 'en');
  }, [watchedLanguage]);

  useEffect(() => {
    // Este efecto se ejecuta cuando cambia currentMemberInput
    // No necesita hacer nada, solo es para trigger re-renders
  }, [currentMemberInput]);

  // 1. Cargar áreas práctica del despacho
  useEffect(() => {
    const fetchPracticeAreas = async () => {
      if (!office?.id) return;
      
      setLoadingPracticeAreas(true);
      try {
        const { data, error } = await supabase
          .from('office_hierarchy')
          .select(`
            id,
            hierarchy:hierarchy_id (
              id,
              practice_area (
                id,
                name
              )
            )
          `)
          .eq('office_id', office.id);

        if (error) throw error;

        const areasMap = new Map();
        data?.forEach(item => {
          const area = item.hierarchy?.practice_area;
          if (area && !areasMap.has(area.id)) {
            areasMap.set(area.id, {
              id: area.id,
              practice_area_id: area.id,
              name: area.name
            });
          }
        });
        
        setPracticeAreas(Array.from(areasMap.values()));
      } catch (error) {
        console.error('Error fetching practice areas:', error);
        showSnackbar(texts[currentLanguage].unexpectedError, 'error');
      } finally {
        setLoadingPracticeAreas(false);
      }
    };

    fetchPracticeAreas();
  }, [office?.id, currentLanguage]);

  // 2. Cargar forms cuando cambia el área práctica
  useEffect(() => {
    const fetchOfficeForms = async () => {
      if (!watchedPracticeArea) {
        setOfficeForms([]);
        setCaseTypes([]);
        setOfficeStatuses([]);
        setValue('form', '');
        setValue('tipo', '');
        setValue('status', '');
        return;
      }

      setLoadingForms(true);
      try {
        // CAMBIO: Usar directamente el ID de office_practice_area
        const { data, error } = await supabase
          .from('office_hierarchy')
          .select(`
            hierarchy:hierarchy_id (
              id,
              practice_area_id,
              hierarchy_form (
                id,
                name,
                display_order
              )
            )
          `)
          .eq('office_id', office.id);

        if (error) throw error;

        const forms = (data || [])
          .filter(item => item.hierarchy?.practice_area_id === watchedPracticeArea)
          .flatMap(item => item.hierarchy?.hierarchy_form || [])
          .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

        setOfficeForms(forms);
        
        // Limpiar campos dependientes
        setValue('form', '');
        setValue('tipo', '');
        setValue('status', '');
        setCaseTypes([]);
        setOfficeStatuses([]);
      } catch (error) {
        console.error('Error fetching office forms:', error);
        showSnackbar(texts[currentLanguage].unexpectedError, 'error');
        setOfficeForms([]);
      } finally {
        setLoadingForms(false);
      }
    };

    fetchOfficeForms();
  }, [watchedPracticeArea, office?.id, setValue, currentLanguage]);

  // 3. Cargar tipos de expediente cuando cambia la forma
  useEffect(() => {
    const fetchCaseTypes = async () => {
      if (!watchedForm) {
        setCaseTypes([]);
        setOfficeStatuses([]);
        setValue('tipo', '');
        setValue('status', '');
        return;
      }

      setLoadingCaseTypes(true);
      try {
        const { data, error } = await supabase
          .from('case_type')
          .select('id, name')
          .eq('hierarchy_form_id', watchedForm);

        if (error) throw error;

        setCaseTypes(data || []);
        
        // Limpiar campos dependientes
        setValue('tipo', '');
        setValue('status', '');
        setOfficeStatuses([]);
      } catch (error) {
        console.error('Error fetching case types:', error);
        showSnackbar(texts[currentLanguage].unexpectedError, 'error');
        setCaseTypes([]);
      } finally {
        setLoadingCaseTypes(false);
      }
    };

    fetchCaseTypes();
  }, [watchedForm, setValue, currentLanguage]);

  // 4. Cargar estatus cuando cambia la forma o el tipo
  useEffect(() => {
    const fetchOfficeStatuses = async () => {
      if (!watchedForm) {
        setOfficeStatuses([]);
        setValue('status', '');
        return;
      }

      setLoadingStatuses(true);
      try {
        // Si hay tipos disponibles pero no se ha seleccionado ninguno, no cargar estatus todavía
        if (caseTypes.length > 0 && !watchedType) {
          setOfficeStatuses([]);
          setValue('status', '');
          setLoadingStatuses(false);
          return;
        }

        let query = supabase
          .from('office_status')
          .select('id, name, display_order')
          .eq('form_id', watchedForm)
          .order('display_order');

        // Filtrar por tipo si existe, o por tipo NULL si no hay tipos
        if (caseTypes.length > 0 && watchedType) {
          query = query.eq('type_id', watchedType);
        } else if (caseTypes.length === 0) {
          query = query.is('type_id', null);
        }

        const { data: statusData, error } = await query;

        if (error) throw error;

        // NUEVO: Cargar conteo de tareas para cada estatus
        if (statusData && statusData.length > 0) {
          const statusIds = statusData.map(s => s.id);
          
          const { data: taskCounts, error: taskError } = await supabase
            .from("task_template")
            .select("office_status_id")
            .in('office_status_id', statusIds);

          if (taskError) {
            console.error('Error loading task counts:', taskError);
            // Continuar sin conteos si hay error
            setOfficeStatuses(statusData.map(s => ({ ...s, task_count: 0 })));
          } else {
            // Contar tareas por estatus
            const countMap = (taskCounts || []).reduce((acc, task) => {
              acc[task.office_status_id] = (acc[task.office_status_id] || 0) + 1;
              return acc;
            }, {});

            // Agregar el conteo a cada estatus
            const statusesWithCount = statusData.map(status => ({
              ...status,
              task_count: countMap[status.id] || 0
            }));

            setOfficeStatuses(statusesWithCount);
          }
        } else {
          setOfficeStatuses([]);
        }
        
        // Limpiar estatus seleccionado
        setValue('status', '');
      } catch (error) {
        console.error('Error fetching office statuses:', error);
        showSnackbar(texts[currentLanguage].unexpectedError, 'error');
        setOfficeStatuses([]);
      } finally {
        setLoadingStatuses(false);
      }
    };

    fetchOfficeStatuses();
  }, [watchedForm, watchedType, caseTypes.length, setValue, currentLanguage]);

  // Generar número de expediente único
  const generateCaseNumber = async () => {
    try {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const uuid = crypto.randomUUID().substring(0, 8).toUpperCase();
      
      const { data: lastCase, error: fetchError } = await supabase
        .from('case')
        .select('number')
        .eq('office_id', office.id)
        .order('created_at', { ascending: false })
        .limit(1);

      if (fetchError) {
        console.error('Error fetching last case:', fetchError);
        throw fetchError;
      }

      let nextSequential = 1;
      if (lastCase && lastCase.length > 0) {
        const lastNumber = lastCase[0].number;
        const match = lastNumber.match(/EXP-(\d+)/);
        if (match) {
          nextSequential = parseInt(match[1]) + 1;
        }
      }

      const sequentialPart = String(nextSequential).padStart(3, '0');
      const caseNumber = `EXP-${sequentialPart}-${day}${month}${year}-${uuid}`;
      
      return caseNumber;
    } catch (error) {
      console.error('Error generating case number:', error);
      throw error;
    }
  };

  // Generar número al cargar el componente
  useEffect(() => {
    const initializeCaseNumber = async () => {
      try {
        const number = await generateCaseNumber();
        setValue('numero', number);
      } catch (error) {
        console.error('Error initializing case number:', error);
        showSnackbar(texts[currentLanguage].generateError, 'error');
      }
    };

    if (office?.id) {
      initializeCaseNumber();
    }
  }, [office?.id, setValue, currentLanguage]);

  const fetchMemberOptions = async (input, roleFilter = null) => {
    if (input.length < 3) return;

    try {
      let query = supabase
        .from('office_member')
        .select(`
          id,
          job_position,
          role,
          person:member_id (*)
        `)
        .eq('office_id', office.id);

      // Filtrar por rol si es necesario (client, lawyer, partner)
      if (roleFilter) {
        query = query.eq('role', roleFilter);
      }

      const { data, error } = await query.limit(10);

      if (!error && data) {
        const transformedData = data.map(item => ({
          id: item.person.id,
          full_name: item.person.full_name,
          email: item.person.email,
          document_number: item.person.document_number,
          phone_number: item.person.phone_number,
          role: item.role,
          job_position: item.job_position
        }));
        setMemberOptions(transformedData);
      } else {
        setMemberOptions([]);
      }
    } catch (error) {
      console.error('Error fetching member options:', error);
      setMemberOptions([]);
    }
  };

  const handleAddMember = () => {
    if (!currentMemberInput) {
      showSnackbar(t.mustEnterName || 'Debe ingresar un nombre o seleccionar una persona', 'warning');
      return;
    }

    // Validar que no se repita la persona
    const isObject = typeof currentMemberInput === 'object';
    const personId = isObject ? currentMemberInput.id : null;
    
    if (personId && caseMembers.some(m => m.person_id === personId)) {
      showSnackbar(t.memberAlreadyAdded || 'Esta persona ya fue agregada', 'warning');
      return;
    }

    // Validaciones de rol
    const clientCount = caseMembers.filter(m => m.role === 'client').length;
    const lawyerCount = caseMembers.filter(m => m.role === 'lawyer').length;

    if (currentMemberRole === 'client' && clientCount >= 1) {
      showSnackbar(t.onlyOneClient || 'Solo puede haber un cliente por expediente', 'warning');
      return;
    }

    if (currentMemberRole === 'lawyer' && lawyerCount >= 1) {
      showSnackbar(t.onlyOnePartner || 'Solo puede haber un socio/abogado principal por expediente', 'warning');
      return;
    }

    const newMember = {
      person_id: personId,
      name_str: isObject ? null : currentMemberInput,
      role: currentMemberRole,
      full_name: isObject ? currentMemberInput.full_name : currentMemberInput,
      email: isObject ? currentMemberInput.email : null,
      document_number: isObject ? currentMemberInput.document_number : null,
      phone_number: isObject ? currentMemberInput.phone_number : null,
    };

    setCaseMembers([...caseMembers, newMember]);
    
    setCurrentMemberInput(null);  
    setCurrentMemberRole(null);
    setMemberOptions([]); 
    
    setTimeout(() => {
      setCurrentMemberInput(null);
    }, 0);
  };

  const handleRemoveMember = (index) => {
    const updatedMembers = caseMembers.filter((_, i) => i !== index);
    setCaseMembers(updatedMembers);
  };

  const getRoleLabel = (role) => {
    const labels = {
      es: {
        client: 'Cliente',
        lawyer: 'Abogado',
        partner: 'Colaborador'
      },
      en: {
        client: 'Client',
        lawyer: 'Lawyer',
        partner: 'Partner'
      }
    };
    return labels[currentLanguage][role] || role;
  };

  const onSubmit = async (data) => {
    try {
      // Validar que haya al menos un cliente
      const hasClient = caseMembers.some(m => m.role === 'client');
      /* if (!hasClient) {
        showSnackbar(t.mustAddClient || 'Debe agregar al menos un cliente', 'warning');
        return;
      } */

      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError) {
        console.error('Auth error:', authError);
        showSnackbar(texts[currentLanguage].unexpectedError, 'error');
        return;
      }

      const { numero, form, tipo, status, titulo, descripcion, start_date, end_date } = data;

      const caseData = {
        office_id: office.id,
        created_by: user.id,
        number: numero,
        hierarchy_form_id: form,
        case_type_id: tipo || null,
        office_status_id: status,
        title: titulo,
        description: descripcion,
        language: data.idioma,
        start_date: start_date || null,
        end_date: end_date || null,
      };

      if (!status) {
        showSnackbar('Debes seleccionar un estatus', 'warning');
        return;
      }
      // Insertar el expediente
      const { data: newCase, error: caseError } = await supabase
        .from('case')
        .insert(caseData)
        .select()
        .single();

      if (caseError) {
        console.error('Error al insertar expediente:', caseError);
        showSnackbar(texts[currentLanguage].saveError, 'error');
        return;
      }

      //console.log('Expediente creado:', newCase.id);

      // Insertar los miembros del caso en case_member
      if (caseMembers.length > 0) {
        const caseMembersToInsert = caseMembers.map(member => ({
          case_id: newCase.id,
          person_id: member.person_id,
          name_str: member.name_str,
          role: member.role
        }));

        const { error: membersError } = await supabase
          .from('case_member')
          .insert(caseMembersToInsert);

        if (membersError) {
          console.error('Error al insertar miembros del caso:', membersError);
          showSnackbar('Error al agregar miembros al expediente', 'error');
          
          await supabase
            .from('case')
            .delete()
            .eq('id', newCase.id);
          
          return;
        }

        shareCaseDriveFolder(newCase.id);
      }

      // Crear tareas desde la plantilla si el estatus tiene tareas configuradas
      if (statusTaskCount > 0) {
        try {
          // Obtener las tareas plantilla del estatus
          const { data: taskTemplates, error: templateError } = await supabase
            .from('task_template')
            .select('*')
            .eq('office_status_id', status)
            .order('display_order');

          if (templateError) {
            console.error('Error obteniendo plantillas de tareas:', templateError);
            throw templateError;
          }

          // Crear las tareas para el nuevo expediente
          if (taskTemplates && taskTemplates.length > 0) {
            const tasksToCreate = taskTemplates.map(template => ({
              case_id: newCase.id,
              text: template.name,
              created_by: user.id,
              display_order: template.display_order,
              completed: false
            }));

            const { error: taskError } = await supabase
              .from('task')
              .insert(tasksToCreate);

            if (taskError) {
              console.error('Error al crear tareas:', taskError);
              // No detenemos el proceso, solo logueamos el error
              showSnackbar('Expediente creado, pero hubo un error al crear las tareas', 'warning');
            }
          }
        } catch (taskError) {
          console.error('Error al procesar tareas plantilla:', taskError);
          // Continuar aunque falle la creación de tareas
        }
      }

      // Crear la estructura de espacios/carpetas de documentos por defecto
      try {
        const { data: newSpaces, error: spacesError } = await supabase
          .from('document_space')
          .insert([
            { case_id: newCase.id, name: 'Documentación contractual', sort_order: 0, created_by: user.id },
            { case_id: newCase.id, name: 'Preparación del Encargo', sort_order: 1, created_by: user.id },
            { case_id: newCase.id, name: 'Ejecución del Encargo', sort_order: 2, created_by: user.id },
          ])
          .select();

        if (spacesError) throw spacesError;

        const contractualSpace = newSpaces?.find(s => s.name === 'Documentación contractual');
        if (contractualSpace) {
          const { error: foldersError } = await supabase
            .from('document_folder')
            .insert([
              { case_id: newCase.id, space_id: contractualSpace.id, name: '1. Documentación del Cliente (Dni, NIE, Pasaporte)', parent_id: null, created_by: user.id },
              { case_id: newCase.id, space_id: contractualSpace.id, name: '2. Hoja de Encargo Profesional y Designación', parent_id: null, created_by: user.id },
            ]);

          if (foldersError) throw foldersError;
        }
      } catch (docStructureError) {
        console.error('Error al crear estructura de documentos por defecto:', docStructureError);
      }

      showSnackbar(texts[currentLanguage].caseCreatedSuccess, 'success');
      
      // Redirigir después de un breve delay
      setTimeout(() => {
        navigate(`/office/${office.id}/home`);
      }, 1500);

    } catch (e) {
      console.error('Error general en onSubmit:', e);
      showSnackbar(texts[currentLanguage].unexpectedError, 'error');
    }
  };

  const t = texts[currentLanguage];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton 
          onClick={() => navigate(-1)}
          sx={{ 
            bgcolor: 'background.paper',
            boxShadow: 1,
            '&:hover': {
              bgcolor: 'action.hover',
            }
          }}
        >
          <ArrowBack />
        </IconButton>
        
        <Typography variant="h4" fontWeight="600" gutterBottom sx={{ mb: 0 }}>
          {t.title}
        </Typography>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          {/* Columna izquierda - Formulario principal */}
          <Grid item size={{xs:12, lg:8}}>
            {/* Información Básica */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
                {t.basicInfo}
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item size={{xs:12, md:8}}>
                  <Controller
                    name="numero"
                    control={control}
                    rules={{ required: t.numberRequired }}
                    render={({ field }) => (
                      <TextField
                        disabled
                        {...field}
                        label={t.caseNumber}
                        fullWidth
                        error={!!errors.numero}
                        helperText={errors.numero?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item size={{xs:12, md:4}}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      {t.caseLanguage}
                    </Typography>
                    <Controller
                      name="idioma"
                      control={control}
                      rules={{ required: t.languageRequired }}
                      render={({ field }) => (
                        <RadioGroup row {...field}>
                          <FormControlLabel value="spanish" control={<Radio />} label={t.spanish} />
                          <FormControlLabel value="english" control={<Radio />} label={t.english} />
                        </RadioGroup>
                      )}
                    />
                  </Box>
                </Grid>

                <Grid item size={{xs:12}}>
                  <Controller
                    name="titulo"
                    control={control}
                    rules={{ required: t.titleRequired }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t.caseTitle}
                        fullWidth
                        error={!!errors.titulo}
                        helperText={errors.titulo?.message}
                      />
                    )}
                  />
                </Grid>

                <Grid item size={{xs:12}}>
                  <Controller
                    name="descripcion"
                    control={control}
                    rules={{ required: t.descriptionRequired }}
                    render={({ field }) => (
                      <TextField 
                        {...field} 
                        label={t.description} 
                        fullWidth 
                        multiline 
                        rows={4} 
                        error={!!errors.descripcion}
                        helperText={errors.descripcion?.message}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Clasificación */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
                {t.classification}
              </Typography>
              
              <Grid container spacing={3}>
                {/* Área Práctica */}
                <Grid item size={{xs:12, md:6}}>
                  <Controller
                    name="practiceArea"
                    control={control}
                    rules={{ required: t.practiceAreaRequired }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label={t.practiceArea}
                        fullWidth
                        disabled={loadingPracticeAreas}
                        error={!!errors.practiceArea}
                        helperText={errors.practiceArea?.message}
                      >
                        {practiceAreas.map((area) => (
                          <MenuItem key={area.id} value={area.id}>
                            {area.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Forma */}
                <Grid item size={{xs:12, md:6}}>
                  <Controller
                    name="form"
                    control={control}
                    rules={{ required: t.formRequired }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label={t.form}
                        fullWidth
                        disabled={!watchedPracticeArea || loadingForms}
                        error={!!errors.form}
                        helperText={
                          errors.form?.message || 
                          (!watchedPracticeArea ? t.selectPracticeAreaFirst : '')
                        }
                      >
                        {officeForms.map((form) => (
                          <MenuItem key={form.id} value={form.id}>
                            {form.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Tipo (Opcional - solo si la forma tiene tipos) */}
                {caseTypes.length > 0 && (
                  <Grid item size={{xs:12, md:6}}>
                    <Controller
                      name="tipo"
                      control={control}
                      rules={{ required: caseTypes.length > 0 ? t.typeRequired : false }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label={t.caseType}
                          fullWidth
                          disabled={!watchedForm || loadingCaseTypes}
                          error={!!errors.tipo}
                          helperText={
                            errors.tipo?.message || 
                            (!watchedForm ? t.selectFormFirst : '')
                          }
                        >
                          {caseTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                )}

                {/* Estatus */}
                <Grid item size={{xs:12}} md={caseTypes.length > 0 ? 6 : 12}>
                  <Controller
                    name="status"
                    control={control}
                    rules={{ required: t.statusRequired }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label={t.status}
                        fullWidth
                        disabled={
                          !watchedForm || 
                          loadingStatuses || 
                          (caseTypes.length > 0 && !watchedType)
                        }
                        error={!!errors.status}
                        helperText={errors.status?.message}
                        onChange={async (e) => {
                          const selectedStatusId = e.target.value;
                          field.onChange(selectedStatusId);

                          const { count, error } = await supabase
                            .from('task_template')
                            .select('*', { count: 'exact'})
                            .eq('office_status_id', selectedStatusId);

                          if (error) {
                            console.error(error);
                            setStatusTaskCount(0);
                          } else {
                            setStatusTaskCount(count || 0);
                          }
                        }}
                      >
                        {officeStatuses.map((status) => (
                          <MenuItem key={status.id} value={status.id}>
                            {status.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                {/* Alerta de tareas plantilla */}
                {watch('status') && (
                  <Grid item size={{xs:12}}>
                    <Alert 
                      severity={statusTaskCount > 0 ? "info" : "warning"}
                    >
                      {statusTaskCount > 0 
                        ? `Este estatus tiene ${statusTaskCount} tarea${statusTaskCount !== 1 ? 's' : ''} plantilla que se ${statusTaskCount !== 1 ? 'crearán' : 'creará'} automáticamente`
                        : "Este estatus no tiene tareas plantilla configuradas"
                      }
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Fechas */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
                {t.dates}
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item size={{xs:12, md:6}}>
                  <Controller
                    name="start_date"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="date"
                        label={t.startDate}
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item size={{xs:12, md:6}}>
                  <Controller
                    name="end_date"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        type="date"
                        label={`${t.endDate} (${t.optional})`}
                        fullWidth
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Columna derecha - Partes Involucradas */}
          <Grid item size={{xs:12, lg:4}}>
            <Paper elevation={2} sx={{ p: 3, position: 'sticky', top: 20 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mb: 3 }}>
                {t.parties}
              </Typography>

              {/* Formulario para agregar miembro */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  {/* Selector de rol */}
                  <Grid item size={{xs:12}}>
                    <TextField
                      select
                      label={t.memberRole}
                      value={currentMemberRole}
                      onChange={(e) => setCurrentMemberRole(e.target.value)}
                      fullWidth
                      size="medium"
                    >
                      <MenuItem 
                        value="client"
                        disabled={caseMembers.some(m => m.role === 'client')}
                      >
                        {t.clientRole}
                      </MenuItem>
                      <MenuItem 
                        value="lawyer"
                        disabled={caseMembers.some(m => m.role === 'lawyer')}
                      >
                        {t.partnerRole}
                      </MenuItem>
                      <MenuItem value="partner">
                        {t.lawyerRole}
                      </MenuItem>
                    </TextField>
                  </Grid>

                  {/* Autocomplete para buscar persona */}
                  <Grid item size={{xs:12}}>
                    <Autocomplete
                      freeSolo
                      loading={loadingMembers}
                      value={currentMemberInput}
                      inputValue={
                        typeof currentMemberInput === 'string' 
                          ? currentMemberInput 
                          : currentMemberInput 
                            ? `${currentMemberInput.full_name.replace(';', '')} | ${currentMemberInput.email || currentMemberInput.document_number}` 
                            : ''
                      }
                      onInputChange={(e, value, reason) => {
                        // Permitir que el input cambie libremente solo cuando el usuario escribe
                        if (reason === 'input') {
                          // Si el usuario está escribiendo, convertir a string
                          setCurrentMemberInput(value);
                        }
                        
                        // No limpiar cuando se selecciona una opción
                        if (reason === 'reset') {
                          // Mantener el valor actual
                          return;
                        }
                        
                        // Buscar opciones cuando hay 3+ caracteres
                        if (value && value.length >= 3) {
                          setLoadingMembers(true);
                          fetchMemberOptions(value, currentMemberRole).finally(() => 
                            setLoadingMembers(false)
                          );
                        }
                      }}
                      onChange={(e, value, reason) => {
                        // Manejar cuando se selecciona una opción del dropdown
                        if (reason === 'selectOption') {
                          setCurrentMemberInput(value);
                        }
                        // Manejar cuando se limpia
                        else if (reason === 'clear') {
                          setCurrentMemberInput(null);
                        }
                      }}
                      options={memberOptions}
                      getOptionLabel={(option) =>
                        typeof option === 'string'
                          ? option
                          : `${option.full_name.replace(';', '')} | ${option.email || option.document_number}`
                      }
                      isOptionEqualToValue={(option, value) => {
                        if (typeof option === 'string' && typeof value === 'string') {
                          return option === value;
                        }
                        if (typeof option === 'object' && typeof value === 'object') {
                          return option.id === value.id;
                        }
                        return false;
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t.searchMember}
                          fullWidth
                          size="medium"
                        />
                      )}
                    />
                    {/* Indicador de estado del miembro */}
                    {currentMemberInput && (
                      <Typography
                        variant="caption"
                        color={typeof currentMemberInput === 'object' ? 'success.main' : 'warning.main'}
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        {typeof currentMemberInput === 'object' 
                          ? t.userIdentified 
                          : t.userNotIdentified
                        }
                      </Typography>
                    )}
                    
                    {/* Advertencia si no hay usuario identificado */}
                    {currentMemberInput && typeof currentMemberInput === 'string' && (
                      <Alert severity="info" sx={{ mt: 1 }}>
                        {currentLanguage === 'es' 
                          ? 'Este miembro se agregará como texto. Podrá registrarlo posteriormente y vincularlo al expediente.'
                          : 'This member will be added as text. You can register them later and link them to the case.'
                        }
                      </Alert>
                    )}
                  </Grid>

                  {/* Botón agregar */}
                  <Grid item size={{xs:12}}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAddMember}
                      disabled={!currentMemberInput}
                    >
                      {t.addMember}
                    </Button>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Lista de miembros agregados */}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  {t.caseMembers}
                </Typography>
                
                {caseMembers.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {t.noMembersYet}
                  </Alert>
                ) : (
                  <List>
                    {caseMembers.map((member, index) => (
                    <Paper 
                      key={index} 
                      elevation={1} 
                      sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body1" fontWeight="600">
                              {member.full_name.replace(';', '')}
                            </Typography>
                            
                            {/* Chip de rol */}
                            <Chip 
                              label={getRoleLabel(member.role)} 
                              size="small"
                              color={office.color}
                            />
                            
                            {/* Chip de tipo de usuario más descriptivo */}
                            <Chip 
                              label={member.person_id ? t.systemUser : t.textLabel}
                              size="small"
                              variant={member.person_id ? "filled" : "outlined"}
                              color={office.color}
                              sx={{ 
                                height: 20,
                                fontSize: '0.65rem'
                              }}
                            />
                          </Box>
                          
                          {member.email && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              ✉️ {member.email}
                            </Typography>
                          )}
                          {member.document_number && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              🆔 {member.document_number}
                            </Typography>
                          )}
                          {member.phone_number && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              📞 {member.phone_number}
                            </Typography>
                          )}
                          
                          {/* MODIFICAR: Mensaje más claro para usuarios no identificados */}
                          {!member.person_id && (
                            <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                              <Typography variant="caption">
                                {currentLanguage === 'es'
                                  ? 'Podrá registrarse y vincularse posteriormente'
                                  : 'Can be registered and linked later'
                                }
                              </Typography>
                            </Alert>
                          )}
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={() => handleRemoveMember(index)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Paper>
                  ))}
                  </List>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Botón de submit */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
          <Button type="submit" variant="contained" size="large" disabled={!isValid}>
            {t.createCase}
          </Button>
        </Box>
      </form>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}